import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import MuxUploader from "@mux/mux-uploader-react";
import { 
  Trash2, RefreshCw, AlertTriangle, X as XIcon, Film, Upload as UploadIcon, 
  Play, Camera, Download, Users, Tag, ExternalLink, MapPin, Calendar, 
  Edit3, Eye, Save, Building2, Briefcase, FileText, Image as ImageIcon,
  Plus, Minus, Link as LinkIcon, Hash, Loader
} from "lucide-react";
import Content360Panel from "../../components/Content360Panel";
import VideoStoreSettings from "../../components/VideoStoreSettings";
import PlatformMatrixOverview from "../../components/PlatformMatrixOverview";
import TwitterScheduleModal from "../../components/TwitterScheduleModal";
import { EDIT_OPTIONS } from "./constants";
import MainVideoSlot from "../../components/MainVideoSlot";
import ScenePhotos from "./ScenePhotos";
import ExtraVideos from "../../components/ExtraVideos";
import VideoVariantsManager from "../../components/VideoVariantsManager";
import VideoPartsPanel from "../../components/VideoPartsPanel";

export default function MuxVideoModal({ video, onClose, onChange, initialView = "edit" }) {
  const [viewMode, setViewMode] = useState(initialView); // "edit" | "preview"
  const [editData, setEditData] = useState({
    title: video?.title || "",
    cast: video?.cast || "",
    shoot_date: video?.shoot_date || "",
    city: video?.city || "",
    location: video?.location || "",
    runtime_minutes: video?.runtime_minutes || "",
    edit_status: video?.edit_status || "pending",
    tags: (video?.tags || []).join(", "),
    is_studio: video?.is_studio || false,
    studio_crm_id: video?.studio_crm_id || null,
    studio_fee: video?.studio_fee || 0,
    studio_fee_currency: video?.studio_fee_currency || "EUR",
    description: video?.description || "",
    members_description: video?.members_description || "",
    vip_description: video?.vip_description || "",
    promo_note: video?.promo_note || "",
    cast_crm_ids: video?.cast_crm_ids || [],
    cameraman_crm_ids: video?.cameraman_crm_ids || [],
    producer_crm_ids: video?.producer_crm_ids || [],
    director_crm_id: video?.director_crm_id || null,
    visibility: video?.visibility || [],
    // External platform links
    onlyfans_vip_link: video?.onlyfans_vip_link || "",
    onlyfans_free_link: video?.onlyfans_free_link || "",
    faphouse_link: video?.faphouse_link || "",
    justforfans_link: video?.justforfans_link || "",
  });
  
  const [saving, setSaving] = useState(false);
  const [crmList, setCrmList] = useState([]);
  const [showTwitterSchedule, setShowTwitterSchedule] = useState(false);
  const [promoPackData, setPromoPackData] = useState(null);
  
  // Escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  
  // Mux-related states (from original)
  const [uploadInfo, setUploadInfo] = useState(null);
  const [creating, setCreating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [mode, setMode] = useState("choose");
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [manualPlaybackId, setManualPlaybackId] = useState("");
  const [grabbedFrames, setGrabbedFrames] = useState([]);
  const [downloadingMp4, setDownloadingMp4] = useState(false);
  const [autoThumbnails, setAutoThumbnails] = useState(video?.auto_thumbnails || []);
  const [extractingThumbnails, setExtractingThumbnails] = useState(false);
  const [showThumbnailPicker, setShowThumbnailPicker] = useState(false);
  const playerRef = useRef(null);
  
  // Trailer
  const [trailerUploadInfo, setTrailerUploadInfo] = useState(null);
  const [trailerCreating, setTrailerCreating] = useState(false);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [generatedTrailerStart, setGeneratedTrailerStart] = useState(video?.generated_trailer_start_time ?? 0);
  const [generatedTrailerEnd, setGeneratedTrailerEnd] = useState(video?.generated_trailer_end_time ?? Math.min(Number(video?.mux_duration) || 30, 30));
  const [generatedTrailerCreating, setGeneratedTrailerCreating] = useState(false);
  
  // Tagged people
  const [taggedPeople, setTaggedPeople] = useState({ cast: [], crew: [], studio: null, director: null });
  
  // Video Variants
  const [showVariantsManager, setShowVariantsManager] = useState(false);

  // Load CRM list for dropdowns
  useEffect(() => {
    api.get("/api/crm/").then(r => {
      setCrmList(r.data || []);
      // Update tagged people
      const byId = new Map(r.data.map(c => [c.id, c]));
      setTaggedPeople({
        cast: (video?.cast_crm_ids || []).map(id => byId.get(id)).filter(Boolean),
        crew: [
          ...(video?.cameraman_crm_ids || []).map(id => byId.get(id)).filter(Boolean).map(c => ({ ...c, _role: "Camera" })),
          ...(video?.producer_crm_ids || []).map(id => byId.get(id)).filter(Boolean).map(c => ({ ...c, _role: "Producer" })),
        ],
        studio: video?.studio_crm_id ? byId.get(video.studio_crm_id) : null,
        director: video?.director_crm_id ? byId.get(video.director_crm_id) : null,
      });
    }).catch(() => {});
  }, [video?.id]);

  // Mux functions (abbreviated - keeping essential ones)
  const loadAssets = async () => {
    setLoadingAssets(true);
    try {
      const r = await api.get("/api/mux/assets?limit=50");
      setAssets(r.data.assets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Mux library");
    } finally { setLoadingAssets(false); }
  };

  const linkAsset = async (playback_id, asset_id) => {
    try {
      const r = await api.post("/api/mux/link", { video_id: video.id, playback_id, asset_id });
      onChange({ id: video.id, mux_playback_id: r.data.playback_id, mux_asset_id: r.data.asset_id, mux_status: "ready", mux_duration: r.data.duration });
      toast.success("Mux asset linked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  };

  const startUpload = async () => {
    setCreating(true);
    try {
      const r = await api.post("/api/mux/upload", { video_id: String(video.id) });
      setUploadInfo({ ...r.data, url: r.data.upload_url || r.data.url });
      onChange({ id: video.id, mux_upload_id: r.data.upload_id, mux_status: "awaiting_upload" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mux upload create failed");
    } finally { setCreating(false); }
  };

  const pollStatus = React.useCallback(async () => {
    try {
      const r = await api.get(`/api/mux/status/${video.id}`);
      onChange({ id: video.id, ...r.data });
      return r.data;
    } catch { return null; }
  }, [video.id, onChange]);

  const removeAsset = async () => {
    if (!window.confirm("Delete Mux video?")) return;
    try {
      await api.delete(`/api/mux/${video.id}`);
      onChange({ id: video.id, mux_upload_id: null, mux_asset_id: null, mux_playback_id: null, mux_status: null, mux_duration: null });
      setUploadInfo(null);
      toast.success("Mux asset deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const captureFrame = async () => {
    const t = Math.max(0, playerRef.current?.currentTime ?? 0);
    try {
      const r = await api.get(`/api/mux/frame-url/${video.id}`, { params: { time: t, width: 1280 } });
      const url = r.data?.url;
      if (!url) throw new Error("No frame URL");
      setGrabbedFrames((prev) => [...prev, { time: t, url }]);
      toast.success(`Captured frame at ${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Capture failed");
    }
  };

  const downloadMp4 = async () => {
    setDownloadingMp4(true);
    try {
      const r = await api.post(`/api/mux/enable-mp4/${video.id}`);
      const url = r.data?.download_url;
      if (!url) throw new Error("No download URL");
      window.open(url, "_blank");
      toast.success("Download URL opened");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setDownloadingMp4(false); }
  };

  // Thumbnail extraction functions
  const extractThumbnails = async () => {
    setExtractingThumbnails(true);
    try {
      const r = await api.post(`/api/mux/extract-thumbnails/${video.id}`);
      setAutoThumbnails(r.data.thumbnails);
      onChange({ id: video.id, auto_thumbnails: r.data.thumbnails });
      setShowThumbnailPicker(true);
      toast.success(`Saved ${r.data.thumbnails.length} thumbnails to Media Gallery`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Thumbnail extraction failed");
    } finally {
      setExtractingThumbnails(false);
    }
  };

  const setThumbnail = async (thumbnailUrl) => {
    try {
      await api.post(`/api/mux/set-thumbnail/${video.id}`, { thumbnail_url: thumbnailUrl });
      onChange({ id: video.id, cover_image: thumbnailUrl });
      toast.success("✓ Thumbnail set as cover image");
      setShowThumbnailPicker(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set thumbnail");
    }
  };

  // Trailer functions
  const startTrailerUpload = async () => {
    setTrailerCreating(true);
    try {
      const r = await api.post("/api/mux/trailer/upload", { video_id: video.id });
      setTrailerUploadInfo({ ...r.data, url: r.data.upload_url || r.data.url });
      setTrailerOpen(true);
      onChange({ id: video.id, trailer_mux_upload_id: r.data.upload_id, trailer_mux_status: "awaiting_upload" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trailer upload create failed");
    } finally { setTrailerCreating(false); }
  };

  const pollTrailerStatus = React.useCallback(async () => {
    try {
      const r = await api.get(`/api/mux/trailer/status/${video.id}`);
      const d = r.data;
      onChange({
        id: video.id,
        trailer_mux_upload_id: d.trailer_mux_upload_id,
        trailer_mux_asset_id: d.trailer_mux_asset_id,
        trailer_mux_playback_id: d.trailer_mux_playback_id,
        trailer_mux_status: d.trailer_mux_status,
        trailer_mux_duration: d.trailer_mux_duration,
      });
      return d;
    } catch { return null; }
  }, [video.id, onChange]);

  const pollGeneratedTrailerStatus = React.useCallback(async () => {
    try {
      const response = await api.get(`/api/mux/generated-trailer/status/${video.id}`);
      onChange({ id: video.id, ...response.data });
      return response.data;
    } catch {
      return null;
    }
  }, [video.id, onChange]);

  useEffect(() => {
    if (video?.generated_trailer_mux_status !== "processing") return undefined;
    const timer = window.setInterval(pollGeneratedTrailerStatus, 5000);
    return () => window.clearInterval(timer);
  }, [video?.generated_trailer_mux_status, pollGeneratedTrailerStatus]);

  const createGeneratedTrailer = async () => {
    const startTime = Number(generatedTrailerStart);
    const endTime = Number(generatedTrailerEnd);
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || startTime < 0 || endTime <= startTime) {
      toast.error("Choose a valid start and end time");
      return;
    }
    if (endTime - startTime > 60) {
      toast.error("The teaser can be at most 60 seconds");
      return;
    }
    if (video?.mux_duration && endTime > Number(video.mux_duration)) {
      toast.error("The end time cannot exceed the video duration");
      return;
    }
    setGeneratedTrailerCreating(true);
    try {
      const response = await api.post(`/api/mux/generated-trailer/${video.id}`, {
        start_time: startTime,
        end_time: endTime,
      });
      onChange({ id: video.id, ...response.data });
      toast.success("Teaser generation started");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not generate the teaser");
    } finally {
      setGeneratedTrailerCreating(false);
    }
  };

  const removeGeneratedTrailer = async () => {
    if (!window.confirm("Delete the app-generated teaser?")) return;
    try {
      await api.delete(`/api/mux/generated-trailer/${video.id}`);
      onChange({
        id: video.id,
        generated_trailer_mux_asset_id: null,
        generated_trailer_mux_playback_id: null,
        generated_trailer_mux_status: null,
        generated_trailer_mux_duration: null,
        generated_trailer_start_time: null,
        generated_trailer_end_time: null,
      });
      toast.success("Generated teaser removed");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not remove the generated teaser");
    }
  };

  const removeTrailer = async () => {
    if (!window.confirm("Delete trailer?")) return;
    try {
      await api.delete(`/api/mux/trailer/${video.id}`);
      onChange({
        id: video.id,
        trailer_mux_upload_id: null, trailer_mux_asset_id: null,
        trailer_mux_playback_id: null, trailer_mux_status: null, trailer_mux_duration: null,
      });
      setTrailerUploadInfo(null);
      setTrailerOpen(false);
      toast.success("Trailer removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // OF Cutter functions
  // Save edit data
  const handleSaveEditData = async () => {
    if (!editData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...editData,
        tags: editData.tags ? editData.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        runtime_minutes: editData.runtime_minutes ? parseInt(editData.runtime_minutes) : null,
      };
      
      if (video.id) {
        // Update existing video
        await api.put(`/api/videos/${video.id}`, payload);
        onChange({ id: video.id, ...payload });
        toast.success("✓ Video details saved");
        
        // Emit data-changed event to trigger reload
        window.dispatchEvent(new CustomEvent('gio:data-changed', { 
          detail: { resourceType: 'videos' } 
        }));
        
        // Check if video has visibility and open Twitter schedule modal
        if (payload.visibility && payload.visibility.length > 0) {
          // Try to fetch promo pack if exists
          try {
            const promoRes = await api.get(`/api/promo/video/${video.id}/latest`);
            if (promoRes.data) {
              setPromoPackData(promoRes.data);
            }
          } catch (err) {
            // No promo pack exists yet, that's ok
          }
          setShowTwitterSchedule(true);
        }
      } else {
        // Create new video
        const response = await api.post("/api/videos/", payload);
        onChange(response.data);
        toast.success("✓ Video created");
        onClose();
      }
    } catch (e) {
      // Handle Pydantic validation errors properly
      const errorDetail = e?.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Pydantic validation error format
        const errors = errorDetail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        toast.error(`Validation error: ${errors}`);
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const playbackId = video?.mux_playback_id;
  const status = video?.mux_status;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-zinc-950 via-zinc-950 to-zinc-900 border border-purple-900/30 shadow-2xl shadow-purple-500/10 max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header with Pink Neon theme */}
        <div className="sticky top-0 z-10 p-5 border-b border-purple-900/30 bg-gradient-to-r from-zinc-900/95 to-purple-950/20 backdrop-blur">
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <div className="overline text-purple-400/80 flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                <Film size={14} /> VIDEO MANAGEMENT
              </div>
              <h2 className="font-heading text-2xl mt-1 text-white truncate">{video.id ? video.title : "New Video"}</h2>
              {status && (
                <div className="text-[13px] mt-1 text-zinc-400">
                  Status: <strong className="capitalize text-pink-400">{status}</strong>
                  {video?.mux_duration ? ` · ${Math.round(video.mux_duration)}s` : ""}
                </div>
              )}
            </div>
            <button 
              onClick={onClose} 
              data-testid="mux-modal-close" 
              className="text-zinc-500 hover:text-pink-400 transition ml-4 p-2 hover:bg-zinc-800 rounded-lg"
            >
              <XIcon size={20} />
            </button>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button 
              onClick={() => setViewMode("edit")}
              className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                viewMode === "edit" 
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30" 
                  : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              }`}
            >
              <Edit3 size={16} /> Edit View
            </button>
            <button 
              onClick={() => setViewMode("preview")}
              className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                viewMode === "preview" 
                  ? "bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg shadow-pink-500/30" 
                  : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              }`}
            >
              <Eye size={16} /> Preview
            </button>
          </div>
        </div>

        <div className="p-6">
          {viewMode === "edit" ? (
            <EditViewComplete 
              video={video}
              editData={editData}
              setEditData={setEditData}
              saving={saving}
              handleSaveEditData={handleSaveEditData}
              crmList={crmList}
              playbackId={playbackId}
              status={status}
              playerRef={playerRef}
              captureFrame={captureFrame}
              downloadMp4={downloadMp4}
              downloadingMp4={downloadingMp4}
              grabbedFrames={grabbedFrames}
              removeAsset={removeAsset}
              trailerUploadInfo={trailerUploadInfo}
              trailerOpen={trailerOpen}
              startTrailerUpload={startTrailerUpload}
              trailerCreating={trailerCreating}
              removeTrailer={removeTrailer}
              pollTrailerStatus={pollTrailerStatus}
              uploadInfo={uploadInfo}
              startUpload={startUpload}
              creating={creating}
              mode={mode}
              setMode={setMode}
              assets={assets}
              loadingAssets={loadingAssets}
              loadAssets={loadAssets}
              linkAsset={linkAsset}
              manualPlaybackId={manualPlaybackId}
              setManualPlaybackId={setManualPlaybackId}
              polling={polling}
              pollStatus={pollStatus}
              onChange={onChange}
              taggedPeople={taggedPeople}
              autoThumbnails={autoThumbnails}
              extractThumbnails={extractThumbnails}
              extractingThumbnails={extractingThumbnails}
              setThumbnail={setThumbnail}
              showThumbnailPicker={showThumbnailPicker}
              setShowThumbnailPicker={setShowThumbnailPicker}
              setShowVariantsManager={setShowVariantsManager}
            />
          ) : (
            <PreviewViewComplete 
              video={video}
              playbackId={playbackId}
              status={status}
              taggedPeople={taggedPeople}
            />
          )}
        </div>
      </div>
      
      {/* Video Variants Manager Modal */}
      {showVariantsManager && (
        <VideoVariantsManager
          video={video}
          onClose={() => setShowVariantsManager(false)}
          onUpdate={() => onChange({ id: video.id })}
        />
      )}
    </div>
  );
}

// ==================== COMPLETE EDIT VIEW ====================
function EditViewComplete({ 
  video, editData, setEditData, saving, handleSaveEditData, crmList,
  playbackId, status, playerRef, captureFrame, downloadMp4, downloadingMp4,
  grabbedFrames, removeAsset, trailerUploadInfo, trailerOpen, startTrailerUpload,
  trailerCreating, removeTrailer, pollTrailerStatus,
  uploadInfo, startUpload, creating, mode, setMode, assets, loadingAssets,
  loadAssets, linkAsset, manualPlaybackId, setManualPlaybackId, polling,
  pollStatus, onChange, taggedPeople, autoThumbnails, extractThumbnails, 
  extractingThumbnails, setThumbnail, showThumbnailPicker, setShowThumbnailPicker,
  setShowVariantsManager
}) {
  return (
    <div className="space-y-6">
      {/* Save Button - Sticky at top */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-900/30 rounded-xl">
        <div className="text-sm text-cyan-300">
          <FileText size={16} className="inline mr-2" />
          Edit video details, crew, uploads, and platform status
        </div>
        <button
          onClick={handleSaveEditData}
          disabled={saving}
          className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 text-white font-semibold rounded-lg transition-all shadow-lg shadow-purple-500/30 flex items-center gap-2"
        >
          <Save size={16} />
          {saving ? "Saving..." : "Save All Changes"}
        </button>
      </div>

      {/* Basic Info Section */}
      <div className="bg-zinc-900/50 border border-purple-900/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-purple-400 mb-4 flex items-center gap-2">
          <FileText size={18} /> EDIT · VIDEO
        </h3>
        
        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Title *</label>
            <input
              type="text"
              value={editData.title}
              onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition"
              placeholder="Video title..."
            />
          </div>

          {/* Studio Toggle */}
          <div 
            onClick={() => setEditData({ ...editData, is_studio: !editData.is_studio })}
            className="flex items-center gap-3 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition cursor-pointer"
          >
            <input
              type="checkbox"
              id="is_studio"
              checked={editData.is_studio}
              onChange={(e) => {
                e.stopPropagation();
                setEditData({ ...editData, is_studio: e.target.checked });
              }}
              className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-purple-500 focus:ring-purple-500 cursor-pointer"
            />
            <label htmlFor="is_studio" className="text-sm text-white flex items-center gap-2 cursor-pointer flex-1">
              <Building2 size={18} className="text-purple-400" />
              <span className="font-semibold">Studio production</span>
              <span className="text-xs text-zinc-500">(Toggle on for paid studio collabs)</span>
            </label>
            {editData.is_studio && (
              <span className="text-xs px-2 py-1 rounded font-bold bg-purple-600 text-white">
                ACTIVE
              </span>
            )}
          </div>

          {/* Studio Details (shown when is_studio is true) */}
          {editData.is_studio && (
            <div className="p-5 bg-purple-900/10 border border-purple-700/30 rounded-xl space-y-4">
              <h4 className="text-sm font-bold text-purple-400 flex items-center gap-2">
                <Building2 size={16} />
                Studio Production Details
              </h4>
              
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2 flex items-center gap-2">
                  Studio Name <span className="text-zinc-600">(from CRM)</span>
                </label>
                <select
                  value={editData.studio_crm_id || ""}
                  onChange={(e) => setEditData({ ...editData, studio_crm_id: e.target.value || null })}
                  className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
                >
                  <option value="">Select studio...</option>
                  {crmList.filter(c => c.category === "studio").map(studio => (
                    <option key={studio.id} value={studio.id}>
                      {studio.stage_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Studio Fee</label>
                  <input
                    type="number"
                    value={editData.studio_fee}
                    onChange={(e) => setEditData({ ...editData, studio_fee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Currency</label>
                  <select
                    value={editData.studio_fee_currency}
                    onChange={(e) => setEditData({ ...editData, studio_fee_currency: e.target.value })}
                    className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Grid: Shoot Date, City, Location, Runtime */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Shoot Date</label>
              <input
                type="date"
                value={editData.shoot_date}
                onChange={(e) => setEditData({ ...editData, shoot_date: e.target.value })}
                className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">City</label>
              <input
                type="text"
                value={editData.city}
                onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
                placeholder="City..."
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Location / Venue</label>
              <input
                type="text"
                value={editData.location}
                onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
                placeholder="Location..."
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Runtime (min)</label>
              <input
                type="number"
                value={editData.runtime_minutes}
                onChange={(e) => setEditData({ ...editData, runtime_minutes: e.target.value })}
                className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
                placeholder="0"
              />
            </div>
          </div>

          {/* Edit Status */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Edit Status</label>
            <select
              value={editData.edit_status}
              onChange={(e) => setEditData({ ...editData, edit_status: e.target.value })}
              className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
            >
              {EDIT_OPTIONS.map(opt => (
                <option key={opt.v} value={opt.v}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Tags (comma-separated)</label>
            <input
              type="text"
              value={editData.tags}
              onChange={(e) => setEditData({ ...editData, tags: e.target.value })}
              className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
              placeholder="#bareback, #domination, #master..."
            />
          </div>

          {/* Publish To Tiers */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-3">📢 PUBLISH TO TIERS</label>
            <div className="space-y-3">
              {/* Free Members */}
              <label className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700 transition">
                <input
                  type="checkbox"
                  checked={(editData.visibility || []).includes("free")}
                  onChange={(e) => {
                    const currentVis = Array.isArray(editData.visibility) ? editData.visibility : [];
                    const newVis = e.target.checked
                      ? [...currentVis, "free"]
                      : currentVis.filter(v => v !== "free");
                    setEditData({ ...editData, visibility: newVis });
                  }}
                  className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-gray-500 focus:ring-gray-500"
                />
                <Users size={18} className="text-gray-400" />
                <div className="flex-1">
                  <span className="text-white font-semibold">Free Members</span>
                  <p className="text-xs text-zinc-500 mt-0.5">Shows trailer, pictures, co-models/crew only (not full video)</p>
                </div>
              </label>

              {/* Basic Members (formerly Members) */}
              <label className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700 transition">
                <input
                  type="checkbox"
                  checked={(editData.visibility || []).includes("members")}
                  onChange={(e) => {
                    const currentVis = Array.isArray(editData.visibility) ? editData.visibility : [];
                    const newVis = e.target.checked
                      ? [...currentVis, "members"]
                      : currentVis.filter(v => v !== "members");
                    setEditData({ ...editData, visibility: newVis });
                  }}
                  className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-green-500 focus:ring-green-500"
                />
                <Users size={18} className="text-green-400" />
                <div className="flex-1">
                  <span className="text-white font-semibold">Basic Members</span>
                  <p className="text-xs text-zinc-500 mt-0.5">Can watch full videos (paid tier)</p>
                </div>
              </label>

              {/* VIP Members */}
              <label className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700 transition">
                <input
                  type="checkbox"
                  checked={(editData.visibility || []).includes("vip")}
                  onChange={(e) => {
                    const currentVis = Array.isArray(editData.visibility) ? editData.visibility : [];
                    const newVis = e.target.checked
                      ? [...currentVis, "vip"]
                      : currentVis.filter(v => v !== "vip");
                    setEditData({ ...editData, visibility: newVis });
                  }}
                  className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-purple-500 focus:ring-purple-500"
                />
                <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div className="flex-1">
                  <span className="text-white font-semibold">VIP Members</span>
                  <p className="text-xs text-zinc-500 mt-0.5">Premium tier with exclusive content</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700 transition">
                <input
                  type="checkbox"
                  checked={(editData.visibility || []).includes("bts")}
                  onChange={(e) => {
                    const currentVis = Array.isArray(editData.visibility) ? editData.visibility : [];
                    const newVis = e.target.checked
                      ? [...currentVis, "bts"]
                      : currentVis.filter(v => v !== "bts");
                    setEditData({ ...editData, visibility: newVis });
                  }}
                  className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-orange-500 focus:ring-orange-500"
                />
                <Film size={18} className="text-orange-400" />
                <span className="text-white font-semibold">Behind The Scenes (BTS)</span>
              </label>
            </div>
            <p className="text-xs text-zinc-500 mt-2">✓ Check boxes to publish video to selected tier areas</p>
          </div>
        </div>
      </div>

      {/* Crew Section */}
      <CrewSection editData={editData} setEditData={setEditData} crmList={crmList} />

      {/* Main Video Upload Section */}
      <MainVideoSection 
        video={video}
        playbackId={playbackId}
        status={status}
        playerRef={playerRef}
        captureFrame={captureFrame}
        downloadMp4={downloadMp4}
        downloadingMp4={downloadingMp4}
        grabbedFrames={grabbedFrames}
        removeAsset={removeAsset}
        uploadInfo={uploadInfo}
        startUpload={startUpload}
        creating={creating}
        mode={mode}
        setMode={setMode}
        assets={assets}
        loadingAssets={loadingAssets}
        loadAssets={loadAssets}
        linkAsset={linkAsset}
        manualPlaybackId={manualPlaybackId}
        setManualPlaybackId={setManualPlaybackId}
        polling={polling}
        pollStatus={pollStatus}
        autoThumbnails={autoThumbnails}
        extractThumbnails={extractThumbnails}
        extractingThumbnails={extractingThumbnails}
        setThumbnail={setThumbnail}
        showThumbnailPicker={showThumbnailPicker}
        setShowThumbnailPicker={setShowThumbnailPicker}
      />

      {/* Parts & Cuts Panel (single section — replaces legacy Video Variants block) */}
      <div className="bg-zinc-900/50 border border-purple-900/20 rounded-xl p-6" data-testid="parts-cuts-section">
        <VideoPartsPanel
          video={video}
          onUpdate={onChange}
          onOpenLegacyManager={() => setShowVariantsManager(true)}
        />
      </div>

      {/* Trailer Section */}
      <TrailerSection
        video={video}
        trailerUploadInfo={trailerUploadInfo}
        trailerOpen={trailerOpen}
        startTrailerUpload={startTrailerUpload}
        trailerCreating={trailerCreating}
        removeTrailer={removeTrailer}
        pollTrailerStatus={pollTrailerStatus}
        generatedTrailerStart={generatedTrailerStart}
        setGeneratedTrailerStart={setGeneratedTrailerStart}
        generatedTrailerEnd={generatedTrailerEnd}
        setGeneratedTrailerEnd={setGeneratedTrailerEnd}
        generatedTrailerCreating={generatedTrailerCreating}
        createGeneratedTrailer={createGeneratedTrailer}
        removeGeneratedTrailer={removeGeneratedTrailer}
      />

      {/* Scene Photos */}
      <div className="bg-zinc-900/50 border border-purple-900/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-purple-400 mb-4 flex items-center gap-2">
          <ImageIcon size={18} /> 📸 SCENE PHOTOS
        </h3>
        <ScenePhotos 
          value={video?.scene_photos || []} 
          onChange={(photos) => onChange({ ...video, scene_photos: photos })}
          videoId={video?.id}
        />
      </div>

      {/* Extra Videos */}
      <div className="bg-zinc-900/50 border border-purple-900/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-purple-400 mb-4 flex items-center gap-2">
          <Film size={18} /> 🎬 EXTRA VIDEOS
        </h3>
        <ExtraVideos 
          value={video?.extra_videos || []} 
          onChange={(extras) => onChange({ ...video, extra_videos: extras })}
          videoId={video?.id}
        />
      </div>

      {/* Descriptions Section */}
      <div className="bg-zinc-900/50 border border-purple-900/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-purple-400 mb-4">📝 DESCRIPTIONS</h3>
        
        <div className="space-y-4">
          {/* Store Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              🛍️ STORE DESCRIPTION <span className="text-zinc-500">(Public)</span>
            </label>
            <textarea
              value={editData.description || ""}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none resize-none"
              placeholder="Public store and video detail description..."
            />
          </div>

          {/* Free Members Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              🆓 FREE MEMBERS DESCRIPTION <span className="text-gray-400">(Free Tier)</span>
            </label>
            <textarea
              value={editData.free_description || ""}
              onChange={(e) => setEditData({ ...editData, free_description: e.target.value })}
              rows={3}
              className="w-full bg-zinc-800 border border-gray-500/30 rounded-lg px-4 py-3 text-white focus:border-gray-500 outline-none resize-none"
              placeholder="Description for free members - they see trailer, pictures, and crew info only..."
            />
            <p className="text-xs text-zinc-500 mt-1">Free members cannot watch the full video</p>
          </div>

          {/* Basic Members Description (formerly Members) */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              👤 BASIC MEMBERS DESCRIPTION <span className="text-green-400">(Basic Tier)</span>
            </label>
            <textarea
              value={editData.members_description || ""}
              onChange={(e) => setEditData({ ...editData, members_description: e.target.value })}
              rows={3}
              className="w-full bg-zinc-800 border border-green-500/30 rounded-lg px-4 py-3 text-white focus:border-green-500 outline-none resize-none"
              placeholder="Special description for Basic Members tier..."
            />
          </div>

          {/* VIP Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              👑 VIP DESCRIPTION <span className="text-pink-400">(VIP Only)</span>
            </label>
            <textarea
              value={editData.vip_description || ""}
              onChange={(e) => setEditData({ ...editData, vip_description: e.target.value })}
              rows={3}
              className="w-full bg-zinc-800 border border-pink-500/30 rounded-lg px-4 py-3 text-white focus:border-pink-500 outline-none resize-none"
              placeholder="Exclusive VIP tier description..."
            />
          </div>

          {/* Promo Note */}
          <div>
            <label className="block text-sm font-medium text-white mb-2 flex items-center gap-2">
              <span className="text-blue-400">⚡</span> PROMO NOTE 
              <span className="text-zinc-500 text-xs">(Mood/vibe for AI caption generator)</span>
            </label>
            <textarea
              value={editData.promo_note || ""}
              onChange={(e) => setEditData({ ...editData, promo_note: e.target.value })}
              rows={2}
              className="w-full bg-zinc-800 border border-blue-500/30 rounded-lg px-4 py-3 text-white focus:border-blue-500 outline-none resize-none"
              placeholder="e.g., 'intense power dynamic', 'romantic beach scene', 'raw muscle action'..."
            />
            <p className="text-xs text-zinc-500 mt-1.5">
              💡 AI combines this with cast, tags, runtime to generate promo captions
            </p>
          </div>
        </div>
      </div>

      {/* Store Settings */}
      <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-cyan-400 mb-4">🛍️ VIDEO STORE SETTINGS</h3>
        <VideoStoreSettings 
          video={video} 
          onChange={onChange}
        />
      </div>

      {/* External Platform Links */}
      <div 
        className="rounded-xl p-6 border border-purple-500/30"
        style={{
          background: '#1a1a1a',
        }}
      >
        <h3 
          className="text-lg font-bold mb-1 flex items-center gap-2"
          style={{ color: '#D4AF37' }}
        >
          <ExternalLink size={18} /> EXTERNAL PLATFORM LINKS
        </h3>
        <p style={{ fontSize: '0.75rem', color: '#71717A', marginBottom: '1rem' }}>
          Direct video URLs for free members to upgrade on your platforms
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* OnlyFans VIP */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#FFFFFF' }}>
              🔞 OnlyFans VIP Link
            </label>
            <input
              type="url"
              value={editData.onlyfans_vip_link || ""}
              onChange={(e) => setEditData({ ...editData, onlyfans_vip_link: e.target.value })}
              placeholder="https://onlyfans.com/..."
              className="w-full px-4 py-2.5 rounded-lg text-white outline-none transition"
              style={{
                background: '#121212',
                border: '1px solid rgba(212, 175, 55, 0.2)',
              }}
              onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.2)'}
            />
          </div>

          {/* OnlyFans Free */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#FFFFFF' }}>
              🆓 OnlyFans Free Link
            </label>
            <input
              type="url"
              value={editData.onlyfans_free_link || ""}
              onChange={(e) => setEditData({ ...editData, onlyfans_free_link: e.target.value })}
              placeholder="https://onlyfans.com/..."
              className="w-full px-4 py-2.5 rounded-lg text-white outline-none transition"
              style={{
                background: '#121212',
                border: '1px solid rgba(212, 175, 55, 0.2)',
              }}
              onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.2)'}
            />
          </div>

          {/* Faphouse */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#FFFFFF' }}>
              🏠 Faphouse Link
            </label>
            <input
              type="url"
              value={editData.faphouse_link || ""}
              onChange={(e) => setEditData({ ...editData, faphouse_link: e.target.value })}
              placeholder="https://faphouse.com/..."
              className="w-full px-4 py-2.5 rounded-lg text-white outline-none transition"
              style={{
                background: '#121212',
                border: '1px solid rgba(212, 175, 55, 0.2)',
              }}
              onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.2)'}
            />
          </div>

          {/* JustForFans */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#FFFFFF' }}>
              🍑 JustForFans Link
            </label>
            <input
              type="url"
              value={editData.justforfans_link || ""}
              onChange={(e) => setEditData({ ...editData, justforfans_link: e.target.value })}
              placeholder="https://justfor.fans/..."
              className="w-full px-4 py-2.5 rounded-lg text-white outline-none transition"
              style={{
                background: '#121212',
                border: '1px solid rgba(212, 175, 55, 0.2)',
              }}
              onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.2)'}
            />
          </div>
        </div>

        <p style={{ fontSize: '0.75rem', color: '#71717A', marginTop: '0.75rem' }}>
          💡 These links will be shown to free members as upgrade options
        </p>
      </div>

      {/* Comprehensive Platform Matrix */}
      <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-cyan-400 mb-4">🌐 COMPREHENSIVE PLATFORM MATRIX</h3>
        <PlatformMatrixOverview video={video} />
      </div>

    </div>
  );
}

// Crew Section Component
function CrewSection({ editData, setEditData, crmList }) {
  const actors = crmList.filter(c => ["Model", "Escort"].includes(c.type));
  const cameramen = crmList.filter(c => c.type === "Cameraman");
  const producers = crmList.filter(c => c.type === "Producer");
  const studios = crmList.filter(c => c.type === "Studio");
  const allCrew = [...cameramen, ...producers, ...actors, ...studios];

  return (
    <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
      <h3 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
        <Briefcase size={18} /> 🎬 CREW · LINKED TO CRM
      </h3>
      
      {/* Cast - Models & Escorts Only */}
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
          <Users size={14} className="inline mr-1" />
          CAST (MODELS & ESCORTS) · {editData.cast_crm_ids.length}
        </label>
        <select
          multiple
          value={editData.cast_crm_ids}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
            setEditData({ ...editData, cast_crm_ids: selected });
          }}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none min-h-[150px]"
        >
          {actors.map(c => (
            <option key={c.id} value={c.id}>
              {`${c.stage_name || c.legal_name || c.email}${c.country ? ` · ${c.country}` : ""}`}
            </option>
          ))}
        </select>
        <div className="text-xs text-zinc-500 mt-1">Hold Ctrl/Cmd to select multiple</div>
      </div>

      {/* Studio Single Select */}
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
          <Building2 size={14} className="inline mr-1" />
          STUDIO (SINGLE)
        </label>
        <select
          value={editData.studio_crm_id || ""}
          onChange={(e) => setEditData({ ...editData, studio_crm_id: e.target.value || null })}
          className="w-full bg-zinc-900 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition"
        >
          <option value="">— None —</option>
          {studios.map(c => (
            <option key={c.id} value={c.id}>
              {c.stage_name || c.legal_name || c.email}
            </option>
          ))}
        </select>
      </div>
      
      {/* Cameraman Multi-select */}
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
          <Camera size={14} className="inline mr-1" />
          CAMERAMAN · {editData.cameraman_crm_ids.length}
        </label>
        <select
          multiple
          value={editData.cameraman_crm_ids}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
            setEditData({ ...editData, cameraman_crm_ids: selected });
          }}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none min-h-[120px]"
        >
          {allCrew.map(c => (
            <option key={c.id} value={c.id}>
              {`${c.stage_name || c.legal_name || c.email} · ${c.type}${c.country ? ` · ${c.country}` : ""}`}
            </option>
          ))}
        </select>
        <div className="text-xs text-zinc-500 mt-1">Hold Ctrl/Cmd to select multiple</div>
      </div>

      {/* Producer Multi-select */}
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
          <Briefcase size={14} className="inline mr-1" />
          PRODUCER · {editData.producer_crm_ids.length}
        </label>
        <select
          multiple
          value={editData.producer_crm_ids}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
            setEditData({ ...editData, producer_crm_ids: selected });
          }}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none min-h-[120px]"
        >
          {allCrew.map(c => (
            <option key={c.id} value={c.id}>
              {c.stage_name || c.legal_name || c.email} · {c.type}
            </option>
          ))}
        </select>
        <div className="text-xs text-zinc-500 mt-1">Hold Ctrl/Cmd to select multiple</div>
      </div>
    </div>
  );
}

// Main Video Section Component (abbreviated for space)
function MainVideoSection({ video, playbackId, status, playerRef, captureFrame, downloadMp4, downloadingMp4, grabbedFrames, removeAsset, uploadInfo, startUpload, creating, mode, setMode, assets, loadingAssets, loadAssets, linkAsset, manualPlaybackId, setManualPlaybackId, polling, pollStatus, autoThumbnails, extractThumbnails, extractingThumbnails, setThumbnail, showThumbnailPicker, setShowThumbnailPicker }) {
  return (
    <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
      <h3 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
        <Film size={18} /> MAIN VIDEO
      </h3>
      
      {mode === "upload" && !playbackId ? (
        <div className="space-y-4">
          {!uploadInfo ? (
            <div className="space-y-4">
              {/* Upload Mode Selection */}
              <div className="grid grid-cols-3 gap-3">
                <button 
                  onClick={startUpload} 
                  disabled={creating}
                  className="p-4 border-2 border-cyan-500/30 hover:border-cyan-500 rounded-lg transition text-center"
                >
                  <UploadIcon size={24} className="mx-auto mb-2 text-cyan-400" />
                  <div className="text-sm font-bold text-white">Upload New</div>
                  <div className="text-xs text-zinc-500 mt-1">Direct upload to Mux</div>
                </button>
                
                <button 
                  onClick={() => setMode("manual")} 
                  className="p-4 border-2 border-purple-500/30 hover:border-purple-500 rounded-lg transition text-center"
                >
                  <Hash size={24} className="mx-auto mb-2 text-purple-400" />
                  <div className="text-sm font-bold text-white">Enter Mux ID</div>
                  <div className="text-xs text-zinc-500 mt-1">Link existing asset</div>
                </button>
                
                <button 
                  onClick={() => { setMode("library"); loadAssets(); }} 
                  className="p-4 border-2 border-pink-500/30 hover:border-pink-500 rounded-lg transition text-center"
                >
                  <Film size={24} className="mx-auto mb-2 text-pink-400" />
                  <div className="text-sm font-bold text-white">From Library</div>
                  <div className="text-xs text-zinc-500 mt-1">Choose from Mux</div>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-cyan-500/30 rounded-lg p-8 bg-zinc-900/50">
                <MuxUploader
                  endpoint={uploadInfo.url}
                  onSuccess={() => {
                    toast.success("Upload complete! Processing...");
                    setMode("choose");
                    pollStatus();
                  }}
                  onError={(e) => {
                    toast.error(`Upload failed: ${e?.message || 'Unknown error'}`);
                  }}
                  style={{
                    '--uploader-background-color': '#18181b',
                    '--uploader-text-color': '#ffffff',
                    '--uploader-border-color': '#06b6d4',
                    '--button-background-color': '#06b6d4',
                    '--button-text-color': '#000000',
                    '--progress-bar-fill-color': '#06b6d4',
                  }}
                />
              </div>
              <button 
                onClick={() => setMode("choose")} 
                className="btn-ghost mt-3 text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      ) : mode === "manual" && !playbackId ? (
        <div className="space-y-4 p-6 bg-zinc-900/50 rounded-lg border border-purple-500/30">
          <div className="text-center mb-4">
            <Hash size={32} className="mx-auto mb-2 text-purple-400" />
            <h4 className="font-bold text-white">Enter Mux Playback ID</h4>
            <p className="text-sm text-zinc-400 mt-1">Link an existing Mux video asset</p>
          </div>
          
          <input
            type="text"
            value={manualPlaybackId}
            onChange={(e) => setManualPlaybackId(e.target.value)}
            placeholder="Enter Mux playback ID..."
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
          />
          
          <div className="flex gap-2">
            <button
              onClick={linkAsset}
              disabled={!manualPlaybackId.trim()}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition"
            >
              Link Asset
            </button>
            <button
              onClick={() => { setMode("upload"); setManualPlaybackId(""); }}
              className="px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : mode === "library" && !playbackId ? (
        <div className="space-y-4">
          <div className="text-center mb-4">
            <Film size={32} className="mx-auto mb-2 text-pink-400" />
            <h4 className="font-bold text-white">Choose from Mux Library</h4>
            <p className="text-sm text-zinc-400 mt-1">Select an existing video asset</p>
          </div>
          
          {loadingAssets ? (
            <div className="text-center py-8 text-zinc-400">
              <Loader className="animate-spin mx-auto mb-2" size={32} />
              Loading library...
            </div>
          ) : assets && assets.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto p-2">
              {assets.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => linkAsset(asset.playback_ids?.[0]?.id || asset.playback_id)}
                  className="text-left border-2 border-zinc-700 hover:border-pink-500 rounded-lg p-3 transition"
                >
                  {asset.playback_ids?.[0]?.id || asset.playback_id ? (
                    <div className="bg-black rounded mb-2 aspect-video overflow-hidden">
                      <MuxPlayer
                        playbackId={asset.playback_ids?.[0]?.id || asset.playback_id}
                        style={{ width: "100%", height: "100%" }}
                        streamType="on-demand"
                        autoPlay={false}
                        muted
                      />
                    </div>
                  ) : (
                    <div className="bg-zinc-800 rounded mb-2 aspect-video flex items-center justify-center">
                      <Film size={24} className="text-gray-600" />
                    </div>
                  )}
                  <div className="text-xs space-y-1">
                    <div className="text-white font-medium truncate">{asset.playback_ids?.[0]?.id || asset.playback_id || asset.id}</div>
                    <div className="text-gray-400">{asset.status}</div>
                    {asset.duration && <div className="text-gray-500">{Math.floor(asset.duration)}s</div>}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-400">
              No assets found in library
            </div>
          )}
          
          <button
            onClick={() => setMode("upload")}
            className="w-full px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition"
          >
            Back
          </button>
        </div>
      ) : playbackId ? (
        <div className="relative bg-black rounded-lg overflow-hidden">
          <MuxPlayer
            ref={playerRef}
            playbackId={playbackId}
            metadata={{ video_id: video.id, video_title: video.title }}
            accentColor="#06B6D4"
            style={{ width: "100%", aspectRatio: "16/9", backgroundColor: "#000000" }}
            className="rounded-lg overflow-hidden"
            autoPlay={false}
            muted={false}
          />
          <div className="flex gap-2 mt-3">
            <button onClick={captureFrame} className="btn-primary text-sm flex items-center gap-2">
              <Camera size={14} /> Capture frame
            </button>
            <button 
              onClick={extractThumbnails} 
              disabled={extractingThumbnails}
              className="btn-ghost text-sm flex items-center gap-2"
            >
              <ImageIcon size={14} /> {extractingThumbnails ? "Extracting..." : "Generate 30 Thumbnails"}
            </button>
            <button onClick={downloadMp4} disabled={downloadingMp4} className="btn-ghost text-sm flex items-center gap-2">
              <Download size={14} /> {downloadingMp4 ? "Preparing..." : "Download MP4"}
            </button>
            <button onClick={removeAsset} className="btn-ghost text-red-400 ml-auto text-sm flex items-center gap-2">
              <Trash2 size={14} /> Delete
            </button>
          </div>
          
          {/* Thumbnail Picker */}
          {showThumbnailPicker && autoThumbnails && autoThumbnails.length > 0 && (
            <div className="mt-4 p-4 bg-zinc-950 border border-cyan-800/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-cyan-400">Select Cover Thumbnail</h4>
                <button 
                  onClick={() => setShowThumbnailPicker(false)}
                  className="text-zinc-500 hover:text-white"
                >
                  <XIcon size={16} />
                </button>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {autoThumbnails.map((thumb, i) => (
                  <button
                    key={i}
                    onClick={() => setThumbnail(thumb.url)}
                    className="relative group"
                  >
                    <img 
                      src={thumb.url} 
                      alt={thumb.label}
                      className="w-full aspect-video object-cover rounded border-2 border-cyan-900/30 hover:border-cyan-500 transition"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center rounded">
                      <span className="text-xs text-white font-bold">{thumb.label}</span>
                    </div>
                    {video.cover_image === thumb.url && (
                      <div className="absolute top-1 right-1 bg-cyan-500 text-black rounded-full p-1">
                        <Eye size={12} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {grabbedFrames.length > 0 && (
            <div className="mt-4 grid grid-cols-4 gap-2">
              {grabbedFrames.map((f, i) => (
                <img key={i} src={f.url} alt={`Frame ${i}`} className="w-full aspect-video object-cover rounded border border-cyan-900/30" />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-zinc-400">
          <Film size={48} className="mx-auto mb-3 text-zinc-600" />
          <div>No Mux video yet</div>
          <button onClick={() => setMode("upload")} className="btn-primary mt-3 flex items-center gap-2 mx-auto">
            <UploadIcon size={14} /> Upload to Mux
          </button>
        </div>
      )}
    </div>
  );
}

// Trailer Section
// eslint-disable-next-line no-unused-vars
function TrailerSection({ video, trailerUploadInfo, trailerOpen, startTrailerUpload, trailerCreating, removeTrailer, pollTrailerStatus, generatedTrailerStart, setGeneratedTrailerStart, generatedTrailerEnd, setGeneratedTrailerEnd, generatedTrailerCreating, createGeneratedTrailer, removeGeneratedTrailer }) {
  return (
    <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-cyan-400 flex items-center gap-2">
          🎬 TRAILER {video?.trailer_mux_duration ? `· ${Math.round(video.trailer_mux_duration)}s` : ""}
        </h3>
        {video?.trailer_mux_playback_id && (
          <button onClick={removeTrailer} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>
      
      {trailerOpen && trailerUploadInfo ? (
        <div className="space-y-4">
          <MuxUploader
            endpoint={trailerUploadInfo.url}
            onSuccess={() => {
              toast.success("Trailer upload complete!");
              pollTrailerStatus();
            }}
            onError={(e) => toast.error(`Trailer upload failed: ${e?.message || 'Unknown error'}`)}
          />
          <button onClick={() => {}} className="btn-ghost text-sm w-full">Cancel</button>
        </div>
      ) : video?.trailer_mux_playback_id ? (
        <div className="relative bg-black rounded-lg overflow-hidden">
          <MuxPlayer
            playbackId={video.trailer_mux_playback_id}
            metadata={{ video_id: video.id, video_title: `${video.title} — Trailer` }}
            accentColor="#06B6D4"
            style={{ width: "100%", aspectRatio: "16/9", backgroundColor: "#000000" }}
            className="rounded-lg overflow-hidden"
            autoPlay={false}
            muted={false}
          />
        </div>
      ) : (
        <button onClick={startTrailerUpload} disabled={trailerCreating} className="w-full py-4 border-2 border-dashed border-zinc-700 hover:border-cyan-500 rounded-lg text-zinc-400 hover:text-cyan-400 transition">
          {trailerCreating ? "Preparing..." : "Upload Trailer"}
        </button>
      )}

      <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="generated-trailer-editor">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-white">App-generated free teaser</h4>
            <p className="mt-1 text-xs text-zinc-500">Create a separate public clip from the selected section. Maximum 60 seconds.</p>
          </div>
          {video?.generated_trailer_mux_asset_id && <button type="button" onClick={removeGeneratedTrailer} className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300"><Trash2 size={12} /> Remove generated</button>}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <label className="block text-xs text-zinc-400">Start (seconds)
            <input type="number" min="0" max={Math.max(0, Number(video?.mux_duration) || 0)} step="0.1" value={generatedTrailerStart} onChange={(event) => setGeneratedTrailerStart(event.target.value)} className="mt-1 min-h-10 w-full rounded border border-zinc-700 bg-zinc-950 px-3 text-sm text-white" />
          </label>
          <label className="block text-xs text-zinc-400">End (seconds)
            <input type="number" min={Number(generatedTrailerStart) + 0.1} max={Math.min(Number(video?.mux_duration) || 60, Number(generatedTrailerStart) + 60)} step="0.1" value={generatedTrailerEnd} onChange={(event) => setGeneratedTrailerEnd(event.target.value)} className="mt-1 min-h-10 w-full rounded border border-zinc-700 bg-zinc-950 px-3 text-sm text-white" />
          </label>
          <button type="button" onClick={createGeneratedTrailer} disabled={generatedTrailerCreating || video?.mux_status !== "ready" || Boolean(video?.generated_trailer_mux_asset_id)} className="min-h-10 rounded border border-cyan-500/40 px-3 text-sm font-medium text-cyan-300 hover:bg-cyan-500/10 disabled:cursor-not-allowed disabled:opacity-50">
            {generatedTrailerCreating ? "Starting…" : "Generate teaser"}
          </button>
        </div>
        {!video?.mux_asset_id && <p className="mt-2 text-xs text-amber-300">Upload the main video to Mux before generating a teaser.</p>}
        {video?.generated_trailer_mux_status === "processing" && <p className="mt-3 flex items-center gap-2 text-xs text-amber-300"><RefreshCw size={13} className="animate-spin" /> Processing your teaser…</p>}
        {video?.generated_trailer_mux_status === "errored" && <p role="alert" className="mt-3 text-xs text-red-300">Mux could not process this teaser. Remove it and try a different range.</p>}
        {video?.generated_trailer_mux_status === "ready" && video?.generated_trailer_mux_playback_id && (
          <div className="mt-4 overflow-hidden rounded-md border border-zinc-800 bg-black">
            <MuxPlayer playbackId={video.generated_trailer_mux_playback_id} metadata={{ video_id: video.id, video_title: `${video.title} — Generated Free Teaser` }} accentColor="#06B6D4" style={{ width: "100%", aspectRatio: "16/9", backgroundColor: "#000000" }} autoPlay={false} muted={false} />
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== COMPLETE PREVIEW VIEW ====================
function PreviewViewComplete({ video, playbackId, status, taggedPeople }) {
  const playerRef = useRef(null);
  
  return (
    <div className="space-y-6">
      {/* Video Player */}
      {playbackId ? (
        <div className="rounded-xl overflow-hidden shadow-2xl shadow-cyan-500/20 border border-cyan-900/30">
          <MuxPlayer
            ref={playerRef}
            playbackId={playbackId}
            metadata={{ video_id: video.id, video_title: video.title }}
            accentColor="#06B6D4"
            style={{ width: "100%", aspectRatio: "16/9" }}
          />
        </div>
      ) : (
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-cyan-900/30 rounded-xl p-16 text-center">
          <Film size={64} className="mx-auto mb-4 text-cyan-400/30" />
          <div className="text-xl font-bold text-zinc-400 mb-2">No video linked yet</div>
          <div className="text-sm text-zinc-600">Switch to Edit View to upload a video</div>
        </div>
      )}

      {/* Video Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {status && (
          <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-900/30 rounded-lg p-4">
            <div className="text-xs uppercase text-cyan-400 mb-1">Status</div>
            <div className="text-lg font-bold text-white capitalize">{status}</div>
          </div>
        )}
        {video?.shoot_date && (
          <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-900/30 rounded-lg p-4">
            <div className="text-xs uppercase text-cyan-400 mb-1">Shoot Date</div>
            <div className="text-lg font-bold text-white">{video.shoot_date}</div>
          </div>
        )}
        {video?.runtime_minutes && (
          <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-900/30 rounded-lg p-4">
            <div className="text-xs uppercase text-cyan-400 mb-1">Runtime</div>
            <div className="text-lg font-bold text-white">{video.runtime_minutes} min</div>
          </div>
        )}
        {video?.city && (
          <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-900/30 rounded-lg p-4">
            <div className="text-xs uppercase text-cyan-400 mb-1"><MapPin size={12} className="inline mr-1" />City</div>
            <div className="text-lg font-bold text-white">{video.city}</div>
          </div>
        )}
      </div>

      {/* Location & Description */}
      {(video?.location || video?.description) && (
        <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
          {video?.location && (
            <div className="mb-4">
              <div className="text-xs uppercase text-cyan-400 mb-2 flex items-center gap-2">
                <MapPin size={14} /> Location
              </div>
              <div className="text-white">{video.location}</div>
            </div>
          )}
          {video?.description && (
            <div>
              <div className="text-xs uppercase text-cyan-400 mb-2">Description</div>
              <div className="text-zinc-300 whitespace-pre-wrap">{video.description}</div>
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {video?.tags?.length > 0 && (
        <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
          <div className="text-xs uppercase text-cyan-400 mb-3 flex items-center gap-2">
            <Tag size={14} /> Tags
          </div>
          <div className="flex flex-wrap gap-2">
            {video.tags.map(t => (
              <span key={t} className="px-3 py-1.5 rounded-full bg-cyan-900/20 text-cyan-300 text-sm font-medium border border-cyan-900/30">
                #{t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Cast & Crew */}
      {(taggedPeople.cast.length > 0 || taggedPeople.crew.length > 0 || taggedPeople.studio || taggedPeople.director) && (
        <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
          <h4 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
            <Users size={18} />
            CAST & CREW ({taggedPeople.cast.length + taggedPeople.crew.length + (taggedPeople.studio ? 1 : 0) + (taggedPeople.director ? 1 : 0)})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {[
              ...(taggedPeople.studio ? [{ ...taggedPeople.studio, _role: "Studio" }] : []),
              ...(taggedPeople.director ? [{ ...taggedPeople.director, _role: "Director" }] : []),
              ...taggedPeople.cast.map(c => ({ ...c, _role: "Cast" })),
              ...taggedPeople.crew,
            ].map(p => (
              <div key={`${p.id}-${p._role}`} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-cyan-900/10">
                {p.photos?.[0] ? (
                  <img src={p.photos[0]} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-cyan-900/30" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-800 to-blue-900 flex items-center justify-center">
                    <Users size={20} className="text-cyan-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white text-sm truncate">
                    {p.stage_name || p.legal_name || p.email}
                  </div>
                  <div className="text-xs text-cyan-400 uppercase font-medium">
                    {p._role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content 360° ROI */}
      {video?.id && (
        <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
          <Content360Panel videoId={video.id} />
        </div>
      )}

      {/* Platform Links */}
      {(video?.of_vip_url || video?.of_free_url || video?.jff_url || video?.rfc_url || video?.faphouse_url) && (
        <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-xl p-6">
          <h4 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
            <ExternalLink size={18} />
            PUBLISHED ON
          </h4>
          <div className="flex flex-wrap gap-3">
            {[
              { l: "OF VIP", u: video.of_vip_url, c: "#00AFF0" },
              { l: "OF Free", u: video.of_free_url, c: "#22c55e" },
              { l: "JFF", u: video.jff_url, c: "#e11d48" },
              { l: "RFC", u: video.rfc_url, c: "#f59e0b" },
              { l: "Faphouse", u: video.faphouse_url, c: "#8b5cf6" },
            ].filter(x => x.u).map(x => (
              <a key={x.l} href={x.u} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 font-semibold text-sm hover:scale-105 transition-all shadow-lg"
                style={{ borderColor: x.c, color: x.c, background: `${x.c}15` }}>
                {x.l} <ExternalLink size={14} />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
